import React, { useEffect, useState, useMemo } from "react";
import { DownChevronIcon, GarmentIcon, ModelIcon, SendIcon } from "../Svg";
import { Button } from "../Button";
import _ from "lodash";
import cx from "classnames";
import { AIShowcase } from "../../store/models/AIShowcase";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ModalTypes } from "../../constants";
import { StableViton } from "../../constants/sd-models";
import Loader from "../Loader";
import UIState, { selectMobileLayout } from "../../store/UIState";
import { createSelector } from "reselect";
import { ImageInput } from "../form/ImageInput";
import { TextInput } from "../form/TextInput";
import { ShowcaseSettingsPanel } from "./ShowcaseSettingsPanel";
import { ShowcaseGallery } from "./ShowcaseGallery";
import {
  AiOndemandApi, PREDICTION_STARTED, PREDICTION_STOPPED, selectApiKeys,
  selectPredictionId, selectPredictionStatus
} from "../../store/models/AiOndemandApi";
import { extractErrorText, getOndemandErrorMsg, readFileAsDataURL } from "../../utils";
import { selectCurrentUser } from "../../store/models/User";
import { PredictionStatus } from "../../constants";
import { selectOndemandModels } from "../../store/models/Ai";
import { useParams } from "react-router";



const LoadingItem = ({ loadingData, elapsedTime, predictionStatus }) => {
  const isLoadingPrediction = predictionStatus === PredictionStatus.IN_PROGRESS
    || predictionStatus === PredictionStatus.STARTED;
  if (!loadingData && !isLoadingPrediction) return <></>;

  if (loadingData && loadingData.msg === 'estimation') {
    return (
      <div className={cx("ResultBox")}>
        <div className={"ResultBox__Loading"}>
          <div className={"ResultBox__Loading__TopWrapper"}>
            Scheduled | {elapsedTime}s / {loadingData.rank_eta?.toFixed(0) ?? '?'}s
          </div>

          <div className={"ResultBox__Loading__CenterWrapper"}>
            <Loader size={"small"} color={"green"} /> Waiting for available slot...
          </div>

          <div className={"ResultBox__Loading__BottomWrapper"}>Number of jobs ahead of you: <span
            style={{ color: '#ffffff' }}>{loadingData.rank + 1}</span></div>
        </div>
      </div>)
  }

  if ((loadingData && loadingData.msg === 'process_starts')
    || isLoadingPrediction) {
    const eta = loadingData?.rank_eta?.toFixed(0);
    const etaString = eta ? `/ ${eta}s` : '';
    return (<div className={cx("ResultBox")}>

      <div className={"ResultBox__Loading"}>

        <div className={"ResultBox__Loading__TopWrapper"}>
          {`Processing | ${elapsedTime}s ${etaString}`}
        </div>

        <div className={"ResultBox__Loading__CenterWrapper"}>
          <Loader color={"green"} />
        </div>

        <div className={"ResultBox__Loading__BottomWrapper"}>Generation in progress...</div>
      </div>
    </div>)
  }
}

const PromptItem = ({ message }) => {
  const [paramsOpened, setParamsOpened] = useState(false);

  const onToggleParams = () => {
    setParamsOpened(!paramsOpened);
  }

  return (
    <div className={cx("PromptContainerRoot")}>
      <div className={"PromptContainer"}>
        <div className={"PromptContainer__Visible"}>
          <div className="PromptContainer__Left">
            <div className={"PromptContainer__PromptLabel"}>Prompt</div>
            <div className={`PromptContainer__ParamsButton ${paramsOpened && "open"}`}
              onClick={onToggleParams}>More parameters <DownChevronIcon /></div>
          </div>
          <div className={"PromptContainer__Right"}>
            <a href={message.model} target={"_blank"}>
              <img src={message.model} />
            </a>
            <a href={message.garment} target={"_blank"}>
              <img src={message.garment} />
            </a>
          </div>
        </div>
        {paramsOpened &&
          <div className={"PromptContainer__Params"}>
            <pre>{JSON.stringify(message.params, null, 2)}</pre>
          </div>}
      </div>
    </div>)

}

const ResultItem = ({ item }) => {
  return (
    <div className={cx("ResultBox")}>
      <div className={"ResultBox__Image"}>
        <a href={item.image} target={"_blank"}>
          <img src={item.image} alt={"result"} />
        </a>
      </div>
    </div>
  )
}

const ImageInputInstructions = ({ type }) => {
  const icon = type === 'garment' ? <GarmentIcon /> : <ModelIcon />

  return (<>
    <div>{icon}</div>
    <div><span className={"ImageInput__Label--Green"}>Upload {type} image</span></div>
    <div>or</div>
    <div>drag image here (jpg)</div>
  </>)
}


const selector = createSelector(
  selectMobileLayout,
  selectApiKeys,
  selectPredictionStatus,
  selectPredictionId,
  selectCurrentUser,
  (isMobile, apiKeys, predictionStatus, predictionId, curUser) => ({
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  }
  ))

let vitonIntervalId = null;

export const ShowcaseViton = ({ showcase, mName = null, className }) => {

  const dispatch = useDispatch();

  const {
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  } = useSelector(state => selector(state));
  const params = useParams();
  const { projectId, id } = params;
  const { templates, services } = useSelector(state => selectOndemandModels(state, projectId));
  const template = templates.find(s => s.id === id);
  const service = services.find(s => s.template_id === template.id);
  let samples = {};
  if (service) {
    let prediction = _.get(Object.values(_.get(service, 'predictions')), '0');
    let instructions = _.get(prediction, 'instructions');
    if (instructions) {
      try {
        let instructionsJson = JSON.parse(instructions);
        samples = _.get(instructionsJson, 'samples')
      } catch (e) {
        console.log('e:', e.message);
      }
    }
  }
  const apiKey = _.get(apiKeys, '0.key');
  const [imageUploading, setImageUploading] = useState(false)
  const [loadingData, setLoadingData] = useState(null)
  const [error, setError] = useState(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [currentModel, setCurrentModel] = useState(null)
  const [currentGarment, setCurrentGarment] = useState(null)
  const [modelUrl, setModelUrl] = useState(null)
  const [garmentUrl, setGarmentUrl] = useState(null)
  const [modelFile, setModelFile] = useState(null)
  const [garmentFile, setGarmentFile] = useState(null)
  const [messages, setMessages] = useState([])
  const [clothingType, setClothingType] = useState(
    StableViton.params.find(p => p.name === 'clothing_type')?.default
  )
  const [settings, setSettings] = useState(_.reduce(StableViton.params.filter(p => p.name !== 'clothing_type'), (obj, param) => {
    obj[param.name] = param.default
    return obj
  }, {}))
  const chatboxRef = React.createRef();
  const [showSettings, setShowSettings] = useState(!isMobile)
  const showcaseUrl = showcase?.models?.[0].urls;

  // Memoize the filtered model to prevent unnecessary re-renders
  const settingsPanelModel = useMemo(() => ({
    ...StableViton,
    params: StableViton.params.filter(p => p.name !== 'clothing_type')
  }), []);

  useEffect(() => {
    if (predictionStatus !== PredictionStatus.STOPPED) {
      dispatch({ type: PREDICTION_STOPPED });
    }
    return () => {
      if (vitonIntervalId) {
        clearInterval(vitonIntervalId)
      }
      dispatch(AIShowcase.actions.stopVitonGeneration())
    }
  }, []);

  const onGenerate = async (event) => {
    setError(null)
    event.preventDefault();

    try {
      setMessages([...messages, {
        type: 'prompt',
        garment: currentGarment,
        model: currentModel,
        params: { ...settings, clothing_type: clothingType }
      }, { type: 'loading' }])
      onScrollToBottom()
      if (!mName) {
        // Start the stopwatch immediately for the new POST API
        startStopwatch()
        setLoadingData({ msg: 'process_starts' })
        dispatch(AIShowcase.actions.vitonPredict(showcaseUrl, {
          garment: currentGarment,
          model: currentModel,
          ...settings,
          clothing_type: clothingType
        }, onPredictEvent));
      } else {
        await onHostedGenerate();
      }
    } catch (e) {
      toast.error(e.message)
      console.error(e)
    }
  };

  const onHostedGenerate = async () => {
    dispatch({ type: PREDICTION_STARTED });
    startStopwatch();
    let imagePresignedUrl = {
      garment_img: { filename: garmentUrl },
      model_img: { filename: modelUrl }
    };
    try {
      if (!modelUrl || !garmentUrl) {
        await fetchPreSignUrl();
        if (!modelUrl) {
          await dispatch(AiOndemandApi.actions.uploadImageToBucket(modelFile, _.get(imagePresignedUrl, `model_img.presigned_url`)));
        }
        if (!garmentUrl) {
          await dispatch(AiOndemandApi.actions.uploadImageToBucket(garmentFile, _.get(imagePresignedUrl, `garment_img.presigned_url`)));
        }
      }
      const params = {
        garment: _.get(imagePresignedUrl, 'garment_img.filename'),
        model: _.get(imagePresignedUrl, 'model_img.filename'),
        ...settings,
        clothingType: clothingType
      };

      let res = await dispatch(AiOndemandApi.actions.startHostedPredict(params, mName, apiKey));
      const outputImageUrl = _.get(res, 'body.infer_requests.0.output.image_url');
      if (outputImageUrl) {
        setLoadingData(null)
        stopInterval()
        setMessages((messages) => [...messages.slice(0, -1), { type: 'result', image: outputImageUrl }])
        onScrollToBottom();
      } else if (res) {
        const errMsg = _.get(res, 'body.infer_requests.0.error_message');
        onError(errMsg === 'null' ? null : errMsg);
      }

      async function fetchPreSignUrl() {
        if (!mName || !apiKey) return;
        let res = await dispatch(AiOndemandApi.actions.presignUrl('stable_viton', null, apiKey));
        if (!modelUrl) imagePresignedUrl.model_img = _.get(res, "model_img")
        if (!garmentUrl) imagePresignedUrl.garment_img = _.get(res, "garment_img")
      }
    } catch (e) {
      dispatch({ type: PREDICTION_STOPPED });
      onError(getOndemandErrorMsg(e.message, curUser));
    }
  }

  const onSelectGalleryImage = async (itemIndex, name) => {
    setImageUploading(true)
    try {
      const imageData = !mName
        ? await dispatch(AIShowcase.actions.vitonGetGalleryImage(showcaseUrl, name === 'garment' ? 1 : 0, itemIndex))
        : (name === 'garment'
          ? existingGarments(showcaseUrl, samples)[itemIndex]
          : existingModels(showcaseUrl, samples)[itemIndex]);
      if (name === 'garment') {
        setCurrentGarment(imageData)
        if (mName) setGarmentUrl(existingGarments(showcaseUrl, samples)[itemIndex])
      } else if (name === 'model') {
        setCurrentModel(imageData)
        if (mName) setModelUrl(existingModels(showcaseUrl, samples)[itemIndex])
      }
    } catch (e) {
      toast.error(e.message)
    }
    setImageUploading(false)
  }

  const onImageSelected = async (file, name) => {
    if (!file) return;
    try {
      setImageUploading(true)
      let fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = function () {
        if (name === 'garment') {
          setCurrentGarment(fileReader.result)
        } else if (name === 'model') {
          setCurrentModel(fileReader.result)
        }
        if (mName) {
          if (name === 'garment') {
            setGarmentFile(file)
            setGarmentUrl(null)
          } else if (name === 'model') {
            setModelFile(file)
            setModelUrl(null)
          }
        }
        setImageUploading(false)
      }
      fileReader.onerror = function (error) {
        setImageUploading(false)
        toast.error("Failed to upload image")
      }
    } catch (e) {
      setImageUploading(false)
      toast.error("Failed to upload image")
    }
  }

  const onSettingsChanged = (settings) => {
    setSettings(settings)
  }

  const onPredictEvent = (event) => {
    switch (event.msg) {
      case 'estimation':
        startStopwatch()
        setLoadingData(event)
        break;
      case 'process_starts':
        setLoadingData(loadingData => ({ ...loadingData, msg: 'process_starts' }))
        break;
      case 'process_completed':
        try {
          if (event.success) {
            setLoadingData(null)
            stopInterval()
            // Handle the new API response format - the image is in data[0]
            const imageData = event?.output?.data[0];
            setMessages((messages) => [...messages.slice(0, -1), { type: 'result', image: imageData }])
            onScrollToBottom()
          } else {
            onError("Failed to generate")
          }
        } catch (e) {
          onError("Failed to generate: " + e.message)
        }
        break;
      case 'error':
      case 'unexpected_error':
        onError(event.error)
        break;
    }
  }

  const onError = (error) => {
    setLoadingData(null)
    setMessages((messages) => messages.slice(0, -2))
    stopInterval()
    setError(error ?? "Failed to generate. Try again later")
    error = extractErrorText(error)
    toast.error(error ?? "Failed to generate. Try again later")
  }

  const startStopwatch = () => {

    if (vitonIntervalId) {
      stopInterval()
    }

    const startTime = new Date().getTime()

    vitonIntervalId = setInterval(() => {
      const currentTime = new Date().getTime()
      setElapsedTime(((currentTime - startTime) / 1000).toFixed(0))
    }, 1000)

  }

  const stopInterval = () => {
    clearInterval(vitonIntervalId)
    vitonIntervalId = null
    setElapsedTime(0)
  }

  const onStopGenerating = (event) => {
    event.preventDefault();

    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "Stop generating",
      message: "Are you sure you want to stop the ongoing generation?",
      onConfirm: onConfirmStopGenerating,
      confirmLabel: "Yes",
      cancelLabel: "No",
      size: 's'
    }))
  }

  const onConfirmStopGenerating = () => {
    stopInterval()
    setLoadingData(null)
    setMessages((messages) => messages.slice(0, -2))
    if (mName) {
      dispatch(AiOndemandApi.actions.stopHostedPredict(predictionId, apiKey));
    } else {
      dispatch(AIShowcase.actions.stopVitonGeneration())
    }
  }

  const onRemoveImages = () => {
    setCurrentModel(null)
    setCurrentGarment(null)
  }

  const onRemoveImage = (name) => {
    if (name === 'model') {
      setCurrentModel(null)
    } else if (name === 'garment') {
      setCurrentGarment(null)
    }
  }

  const onScrollToBottom = () => {
    if (!isMobile) {
      setTimeout(() => {
        window.scrollTo(0, document.body.scrollHeight);
        if (!_.isNil(chatboxRef) && !_.isNil(chatboxRef.current)) {
          chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
        }
      }, 200)
    }
  }

  const onToggleSettings = () => {
    setShowSettings(!showSettings);
  }

  return (
    <div className={cx("ShowcaseViton", className)}>
      <div className="ShowcaseViton__container">
        <div className={"ShowcaseViton__LeftSide"}>

          <div className={"SidePanel"}>

            <form className="SidePanel__form">

              <div className={"SidePanel__ImageInputs"}>
                <ImageInput value={currentModel}
                  name={"model"}
                  label={"Model Image"}
                  onChange={onImageSelected}
                  onRemove={onRemoveImage}
                  formats={"image/jpeg, image/jpg, image/png"}
                  instructions={<ImageInputInstructions type={"model"} />}>
                  <div className={"ImageInput__LeftBadge"}>Model</div>
                </ImageInput>

                {(isMobile || mName) && <ShowcaseGallery images={existingModels(showcaseUrl, samples)}
                  title={"Model examples"}
                  onClick={onSelectGalleryImage}
                  name={"model"} />}

                <ImageInput value={currentGarment}
                  name={"garment"}
                  label={"Garment Image"}
                  formats={"image/jpeg, image/jpg, image/png"}
                  onRemove={onRemoveImage}
                  onChange={onImageSelected}
                  instructions={<ImageInputInstructions type={"garment"} />}>
                  <div className={"ImageInput__LeftBadge"}>Garment</div>
                </ImageInput>

                {(isMobile || mName) && <ShowcaseGallery images={existingGarments(showcaseUrl, samples)}
                  title={"Garment examples"}
                  onClick={onSelectGalleryImage}
                  name={"garment"} />}
              </div>
              <div className={"SidePanel__Examples"}>
                {!isMobile && !mName &&
                  <ShowcaseGallery images={existingModels(showcaseUrl, samples)}
                    title={"Model examples"}
                    onClick={onSelectGalleryImage}
                    name={"model"} />}
                {!isMobile && !mName &&
                  <ShowcaseGallery images={existingGarments(showcaseUrl, samples)}
                    title={"Garment examples"}
                    onClick={onSelectGalleryImage}
                    name={"garment"} />}
              </div>

              <div className={"SidePanel__ActionButtons"}>
                <Button
                  title={"Clear"}
                  color={"grey"}
                  className="SidePanel__ActionButtons__Button SidePanel__ActionButtons__Button"
                  disabled={_.isNil(currentModel)}
                  onClick={onRemoveImages}
                />

                {(loadingData || predictionStatus === PredictionStatus.IN_PROGRESS)
                  ? <Button
                    title={"Stop generating"}
                    onClick={onStopGenerating}
                    className="SidePanel__ActionButtons__Button--Primary"
                    color={"red"}
                  />
                  : <Button
                    title={"Generate"}
                    onClick={onGenerate}
                    loading={imageUploading}
                    icon={imageUploading ? null : <SendIcon />}
                    className="SidePanel__ActionButtons__Button SidePanel__ActionButtons__Button--Primary"
                    color={"green"}
                    disabled={!_.isNil(loadingData) || _.isNil(currentModel) || _.isNil(currentGarment)
                      || predictionStatus === PredictionStatus.STARTED} />}
              </div>

              <div className={"SidePanel__ClothingType"}>
                <TextInput
                  label={"Clothing Type"}
                  type={"text"}
                  value={clothingType}
                  onChange={(e) => setClothingType(e.target.value)}
                  placeholder={"e.g., untucked, tucked"}
                />
              </div>

              <div className={`SidePanel__OutputOptions ${showSettings && "open"}`}
                onClick={onToggleSettings}>Output options <DownChevronIcon /></div>

              {showSettings &&
                <>
                  <ShowcaseSettingsPanel
                    model={settingsPanelModel}
                    onParamsChanged={onSettingsChanged}
                  />
                </>}
            </form>

          </div>
        </div>

        <div className={cx("ShowcaseViton__RightSide")}>
          {error && <div className={"ErrorMessage"}>{error}</div>}

          <div className={"MessagesWrapper"} ref={chatboxRef}>
            {!_.isEmpty(messages) && messages.map((item, index) => {
              switch (item.type) {
                case 'prompt':
                  return (<PromptItem key={index} message={item} />)
                case 'loading':
                  return (<LoadingItem key={index} loadingData={loadingData} elapsedTime={elapsedTime} predictionStatus={predictionStatus} />)
                case 'result':
                  return (<ResultItem key={index} item={item} />)
              }
            })}
          </div>

        </div>
      </div>

    </div>
  );
}

const existingModels = (url, samples) => {
  const sampleImages = _.get(samples, 'model_img')
  return sampleImages ? sampleImages : [
    `${url}/file=/app/inputs/upper_models/00071_00.jpg`,
    `${url}/file=/app/inputs/upper_models/00094_00.jpg`,
    `${url}/file=/app/inputs/upper_models/00273_00.jpg`,
    `${url}/file=/app/inputs/upper_models/00814_00.jpg`,
    `${url}/file=/app/inputs/upper_models/02196_00.jpg`,
    `${url}/file=/app/inputs/upper_models/04913_00.jpg`,
  ]
}

const existingGarments = (url, samples) => {
  const sampleImages = _.get(samples, 'garment_img')
  return sampleImages ? sampleImages : [
    `${url}/file=/app/inputs/cloth/00006_00.jpg`,
    `${url}/file=/app/inputs/cloth/00017_00.jpg`,
    `${url}/file=/app/inputs/cloth/00055_00.jpg`,
    `${url}/file=/app/inputs/cloth/00126_00.jpg`,
    `${url}/file=/app/inputs/cloth/00785_00.jpg`,
    `${url}/file=/app/inputs/cloth/00884_00.jpg`,
    `${url}/file=/app/inputs/cloth/00889_00.jpg`,
    `${url}/file=/app/inputs/cloth/00893_00.jpg`,
  ]
}